import { bitbucketClient } from "./bitbucketCore";
import axios from "axios";

// Get pipelines for a repository
export const getRepositoryPipelines = async (workspace, repoSlug) => {
  try {
    console.log(`API: Fetching pipelines for ${workspace}/${repoSlug}`);

    if (!workspace || !repoSlug) {
      console.error("Missing required parameters for getRepositoryPipelines:", {
        workspace,
        repoSlug,
      });
      throw new Error("Workspace and repository slug are required");
    }

    // First, get the pipeline configuration which contains pipeline definitions
    const configResponse = await bitbucketClient.get(
      `/repositories/${workspace}/${repoSlug}/pipelines_config/`
    );
    console.log("API: Pipeline config response:", configResponse.data);

    // Also get recent pipelines to find any custom pipelines that have been run
    const pipelinesResponse = await bitbucketClient.get(
      `/repositories/${workspace}/${repoSlug}/pipelines/?sort=-created_on&pagelen=15`
    );
    console.log("API: Recent pipelines response:", pipelinesResponse.data);

    // Combine the data to get a complete set of pipeline options
    const pipelineConfig = configResponse.data || {};
    if (!pipelineConfig.definitions) {
      pipelineConfig.definitions = [];
    }

    // Extract named pipelines from recent runs to find actual used pipelines
    const recentPipelines = pipelinesResponse.data?.values || [];
    recentPipelines.forEach((pipeline) => {
      if (
        pipeline.target &&
        pipeline.target.selector &&
        pipeline.target.selector.pattern
      ) {
        // If this is a known pipeline type, add it
        const pattern = pipeline.target.selector.pattern;
        const type = pipeline.target.selector.type;

        // Check if this is a deployment pipeline
        const isDeploymentPipeline = pattern
          .toLowerCase()
          .includes("deploy to");

        // Only add if we don't already have it
        const existingDefinition = pipelineConfig.definitions.find(
          (def) => def.selector && def.selector.pattern === pattern
        );

        if (!existingDefinition) {
          // For deployment pipelines, ALWAYS use type "branches" as shown in the Bitbucket UI
          // This is critical for the API to work correctly
          if (isDeploymentPipeline) {
            pipelineConfig.definitions.push({
              selector: {
                type: "branches", // MUST be "branches" for deployment pipelines
                pattern: pattern,
              },
              displayName: pattern,
            });
          }
          // For other non-branch pipelines, use the type as provided
          else if (type !== "branches") {
            pipelineConfig.definitions.push({
              selector: {
                type: type,
                pattern: pattern,
              },
              displayName: pattern,
            });
          }
        }
      }
    });

    console.log("API: Combined pipeline data:", pipelineConfig);
    return pipelineConfig;
  } catch (error) {
    console.error("Error fetching repository pipelines:", error);
    console.error("Error details:", error.response?.data || error.message);

    // Return empty pipeline definitions instead of throwing error
    return { definitions: [] };
  }
};

// Run a pipeline on a specific branch with specific pipeline config
export const runPipeline = async (
  workspace,
  repoSlug,
  branch,
  pipelineConfig = {}
) => {
  try {
    console.log(`Original pipeline config:`, pipelineConfig);

    // Create the base payload structure EXACTLY as shown in the Bitbucket UI example:
    // {"target":{"type":"pipeline_ref_target","selector":{"type":"branches","pattern":"Deploy to BU Abroad"},"ref_name":"open-release/sumac.master","ref_type":"branch"}}
    const payload = {
      target: {
        type: "pipeline_ref_target",
        ref_name: branch,
        ref_type: "branch",
      },
    };

    // Add selector based on the pipelineConfig
    if (pipelineConfig.selector) {
      // This is the critical part - ensuring the selector is in the correct format
      // The payload.target.selector must have type: "branches" for deployment pipelines
      const pattern = pipelineConfig.selector.pattern;

      // Based on your Bitbucket UI example, ALL custom named pipelines
      // use type: "branches" not type: "custom"
      if (pattern.toLowerCase().includes("deploy to")) {
        console.log(`Using deployment pipeline with pattern: ${pattern}`);

        // Match the exact structure from your Bitbucket UI example
        payload.target.selector = {
          type: "branches", // IMPORTANT: Using "branches" as shown in your example
          pattern: pattern,
        };
      }
      // For other selector types
      else {
        payload.target.selector = pipelineConfig.selector;
      }
    }

    console.log(
      `API: Running pipeline for ${workspace}/${repoSlug}:${branch} with payload:`,
      JSON.stringify(payload, null, 2)
    );

    const response = await bitbucketClient.post(
      `/repositories/${workspace}/${repoSlug}/pipelines/`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error("Error running pipeline:", error);
    console.error("Error details:", error.response?.data || error.message);
    throw error;
  }
};

// Run pipelines on multiple repositories
export const bulkRunPipelines = async (
  workspace,
  repositories,
  branch,
  pipelineConfig = {}
) => {
  const results = [];

  for (const repo of repositories) {
    try {
      const result = await runPipeline(workspace, repo, branch, pipelineConfig);
      results.push({
        success: true,
        data: result,
        repository: repo,
        branch: branch,
      });
    } catch (error) {
      results.push({
        success: false,
        error,
        repository: repo,
        branch: branch,
      });
    }
  }

  return results;
};

// Get recent pipeline runs for a repository
export const getRepositoryPipelineRuns = async (
  workspace,
  repoSlug,
  params = {}
) => {
  try {
    // Default parameters matching user-provided values
    const queryParams = {
      page: 1,
      pagelen: 20,
      sort: "-created_on",
      fields: [
        "+values.target.commit.message",
        "+values.target.selector.type",
        "+values.target.selector.pattern",
        "+values.target.commit.summary.html",
        "+values.target.*",
        "+values.*",
        "+page",
        "+size",
      ].join(","),
      ...params,
    };

    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join("&");

    const url = `/repositories/${workspace}/${repoSlug}/pipelines/?${queryString}`;
    console.log("Fetching pipelines (user fields):", url);

    const response = await bitbucketClient.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching pipeline runs:", error);
    throw error;
  }
};

// Get a specific pipeline run details
export const getPipelineRunDetails = async (
  workspace,
  repoSlug,
  pipelineUuid
) => {
  try {
    const url = `/repositories/${workspace}/${repoSlug}/pipelines/${pipelineUuid}`;
    const response = await bitbucketClient.get(url);

    return response.data;
  } catch (error) {
    console.error("Error fetching pipeline run details:", error);
    throw error;
  }
};

// Get pipeline steps for a specific pipeline run
export const getPipelineSteps = async (workspace, repoSlug, pipelineUuid) => {
  try {
    // Remove curly braces from pipeline UUID if present
    let normalizedPipelineUuid = pipelineUuid;
    if (pipelineUuid.startsWith("{") && pipelineUuid.endsWith("}")) {
      normalizedPipelineUuid = pipelineUuid.substring(
        1,
        pipelineUuid.length - 1
      );
    }

    // Two possible API endpoints to try
    const urls = [
      `/repositories/${workspace}/${repoSlug}/pipelines/${normalizedPipelineUuid}/steps/`, // Standard API
      `/!api/internal/repositories/${workspace}/${repoSlug}/pipelines/${normalizedPipelineUuid}/steps/`, // Internal API
    ];

    // Try both URLs
    let response;

    console.log(
      `Trying to fetch pipeline steps for ${workspace}/${repoSlug}/${normalizedPipelineUuid}`
    );

    // Try the standard API first
    try {
      console.log(`Trying API endpoint: ${urls[0]}`);
      response = await bitbucketClient.get(urls[0]);
      console.log(`Success with standard API: ${urls[0]}`);
    } catch (err) {
      console.warn(`First endpoint failed: ${urls[0]}`, err.message);

      // Check if this is an "Unexpected response body" error
      if (
        err.response?.data?.error?.detail === "Unexpected response body" ||
        err.response?.status === 204 || // No content but success
        (err.response?.status >= 200 && err.response?.status < 300) // Any success status
      ) {
        console.log(
          "Got 'Unexpected response body' error but the API call may have been successful"
        );
        // Return a placeholder response with empty values array
        return { values: [], size: 0 };
      }

      // Try the internal API as fallback
      try {
        console.log(`Trying fallback API endpoint: ${urls[1]}`);
        response = await bitbucketClient.get(urls[1]);
        console.log(`Success with internal API: ${urls[1]}`);
      } catch (err2) {
        // Check for "Unexpected response body" in the fallback as well
        if (
          err2.response?.data?.error?.detail === "Unexpected response body" ||
          err2.response?.status === 204 || // No content but success
          (err2.response?.status >= 200 && err2.response?.status < 300) // Any success status
        ) {
          console.log(
            "Got 'Unexpected response body' error with fallback API, returning empty response"
          );
          // Return a placeholder response with empty values array
          return { values: [], size: 0 };
        }

        console.error(
          `Both API endpoints failed: ${urls[0]}, ${urls[1]}`,
          err2.message
        );
        throw err2;
      }
    }

    if (!response || !response.data) {
      console.error("No data received from steps API");
      throw new Error("No data received from steps API");
    }

    console.log(
      `Received ${
        response.data.values?.length || 0
      } steps for pipeline ${normalizedPipelineUuid}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching pipeline steps:", error);
    console.error("Error details:", error.response?.data || error.message);

    // Additional fallback for any "Unexpected response body" error that may have been missed
    if (
      error.response?.data?.error?.detail === "Unexpected response body" ||
      error.response?.status === 204 ||
      (error.response?.status >= 200 && error.response?.status < 300)
    ) {
      console.log(
        "Caught 'Unexpected response body' error in final catch block"
      );
      return { values: [], size: 0 };
    }

    throw error;
  }
};

// Trigger a manual pipeline step (e.g., deployments)
export const triggerPipelineStep = async (
  workspace,
  repoSlug,
  pipelineUuid,
  stepUuid,
  retryCount = 2
) => {
  try {
    // Remove curly braces from UUIDs if present
    let normalizedPipelineUuid = pipelineUuid;
    if (pipelineUuid.startsWith("{") && pipelineUuid.endsWith("}")) {
      normalizedPipelineUuid = pipelineUuid.substring(
        1,
        pipelineUuid.length - 1
      );
    }

    let normalizedStepUuid = stepUuid;
    if (stepUuid.startsWith("{") && stepUuid.endsWith("}")) {
      normalizedStepUuid = stepUuid.substring(1, stepUuid.length - 1);
    }

    console.log(
      `Triggering pipeline step: workspace=${workspace}, repo=${repoSlug}, pipeline=${normalizedPipelineUuid}, step=${normalizedStepUuid}`
    );

    // For internal API endpoints, we need to use a different base URL
    // This is a direct URL to the Bitbucket site, not the API
    const internalApiBaseUrl = "https://bitbucket.org";

    // Check if this is a synthetic step ID (created when no real step was found)
    const isSyntheticStep = normalizedStepUuid.endsWith("-deploy");

    // If this is a synthetic step, use a different API endpoint that directly triggers deployment
    let internalApiPath;
    if (isSyntheticStep) {
      // For synthetic steps, we'll use the direct deployment API without a step UUID
      internalApiPath = `/!api/internal/repositories/${workspace}/${repoSlug}/pipelines/${normalizedPipelineUuid}/deployments/trigger`;
      console.log("Using synthetic step deployment endpoint");
    } else {
      // Standard step triggering endpoint
      internalApiPath = `/!api/internal/repositories/${workspace}/${repoSlug}/pipelines/${normalizedPipelineUuid}/steps/${normalizedStepUuid}/start_step`;
    }

    const fullUrl = internalApiBaseUrl + internalApiPath;

    console.log(`Calling Bitbucket API directly: ${fullUrl}`);

    // Using axios directly instead of bitbucketClient to specify the full URL
    const token = localStorage.getItem("bitbucket_token");

    if (!token) {
      console.warn(
        "No Bitbucket token found in localStorage - authentication may fail"
      );
    }

    const requestOptions = {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      // Adding timeout to prevent hanging requests
      timeout: 15000,
    };

    // For synthetic steps, include a minimal payload
    const payload = isSyntheticStep ? { environment: "production" } : "";

    // Post with appropriate payload based on step type
    try {
      const response = await axios.post(fullUrl, payload, requestOptions);
      console.log("API response:", response.data);
      return response.data || { success: true };
    } catch (apiError) {
      // Handle common API errors

      // Check for the specific "Unexpected response body" error - this is actually a success case
      if (
        apiError.response?.data?.error?.detail === "Unexpected response body" ||
        apiError.response?.status === 204 || // No content but success
        (apiError.response?.status >= 200 && apiError.response?.status < 300) // Any success status
      ) {
        console.log(
          "Got 'Unexpected response body' error but the API call was likely successful"
        );
        return { success: true };
      }

      // Check for authorization errors
      if (
        apiError.response?.status === 401 ||
        apiError.response?.status === 403
      ) {
        console.error("Authorization error - token may be invalid or expired");
        throw new Error(
          "Authorization failed. Please log in again to refresh your token."
        );
      }

      // Check for network errors or timeouts
      if (apiError.code === "ECONNABORTED" || !apiError.response) {
        console.error("Network error or timeout when triggering pipeline step");

        // Retry logic
        if (retryCount > 0) {
          console.log(`Retrying... (${retryCount} attempts left)`);
          // Wait 2 seconds before retrying
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return triggerPipelineStep(
            workspace,
            repoSlug,
            pipelineUuid,
            stepUuid,
            retryCount - 1
          );
        }
      }

      // If we get a 404 on the synthetic step endpoint, try the standard deployment endpoint
      if (isSyntheticStep && apiError.response?.status === 404) {
        console.log(
          "Synthetic step endpoint failed, trying standard deployment endpoint"
        );

        // Use an alternative direct deployment endpoint
        const altApiPath = `/!api/internal/repositories/${workspace}/${repoSlug}/pipelines/${normalizedPipelineUuid}/deploy`;
        const altFullUrl = internalApiBaseUrl + altApiPath;

        try {
          const altResponse = await axios.post(
            altFullUrl,
            { environment: "production" },
            requestOptions
          );
          console.log("Alternative API response:", altResponse.data);
          return altResponse.data || { success: true };
        } catch (altError) {
          // If the alt endpoint also fails with "Unexpected response body", it might have worked
          if (
            altError.response?.data?.error?.detail ===
              "Unexpected response body" ||
            altError.response?.status === 204 ||
            (altError.response?.status >= 200 &&
              altError.response?.status < 300)
          ) {
            console.log(
              "Alternative endpoint returned 'Unexpected response body' but may have succeeded"
            );
            return { success: true, status: "possibly_completed" };
          }

          console.error("Both deployment endpoints failed");
          throw altError;
        }
      }

      // Rethrow the error if we couldn't handle it and have no more retries
      throw apiError;
    }
  } catch (error) {
    console.error("Error triggering pipeline step:", error);
    console.error("Error details:", error.response?.data || error.message);

    // Final fallback - even if we got an error, if it's an "Unexpected response body" it might have worked
    if (
      error.response?.data?.error?.detail === "Unexpected response body" ||
      error.response?.status === 204 ||
      (error.response?.status >= 200 && error.response?.status < 300)
    ) {
      console.log(
        "Got 'Unexpected response body' error in final catch block but the operation may have succeeded"
      );
      return { success: true, status: "possibly_completed" };
    }

    throw error;
  }
};

// Stop a running pipeline
export const stopPipelineRun = async (workspace, repoSlug, pipelineUuid) => {
  try {
    const url = `/repositories/${workspace}/${repoSlug}/pipelines/${pipelineUuid}/stopPipeline`;
    const response = await bitbucketClient.post(url);

    return response.data;
  } catch (error) {
    console.error("Error stopping pipeline run:", error);
    throw error;
  }
};

const pipelinesApi = {
  getRepositoryPipelines,
  runPipeline,
  bulkRunPipelines,
  getRepositoryPipelineRuns,
  getPipelineRunDetails,
  getPipelineSteps,
  triggerPipelineStep,
  stopPipelineRun,
};

export default pipelinesApi;
